import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Switch,
} from "react-native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ParentStackParamList } from "../../navigation/types";
import { childrenApi, Child } from "../../api/children";
import { useAuthStore } from "../../state/authStore";
import { ParentTabBar , TAB_BAR_CLEARANCE } from "../../components/ParentTabBar";
import { colors, fontFamily, radii } from "../../theme";
import { CONTENT_CATEGORIES } from "../../data/categories";

type Props = NativeStackScreenProps<ParentStackParamList, "Controls">;

const DAILY_OPTIONS = [15, 30, 45, 60, 90, 120];
const SESSION_OPTIONS: (number | "noLimit")[] = [10, 15, 20, 30, "noLimit"];
// One shared vocabulary with AddChild — see src/data/categories.ts.
const CATEGORIES = CONTENT_CATEGORIES;

export default function ControlsScreen({ navigation, route }: Props) {
  // childId may be omitted (Controls tab tapped from Library/Me). Resolve to
  // the first child below so the screen loads instead of bouncing through Home.
  const [childId, setChildId] = useState<string | undefined>(
    route.params?.childId,
  );
  const { accessToken } = useAuthStore();
  const [child, setChild] = useState<Child | null>(null);
  const [dailyMin, setDailyMin] = useState(60);
  const [sessionMin, setSessionMin] = useState<number>(20); // 0 == no limit
  const [interactiveQ, setInteractiveQ] = useState(true);
  const [autoPlay, setAutoPlay] = useState(false);
  const [bedtimeLock, setBedtimeLock] = useState(false);
  const [selectedCats, setSelectedCats] = useState<string[]>([]);

  // No childId passed → default to the first child so Controls/Stats tabs work
  // in one tap from any screen.
  useEffect(() => {
    if (childId || !accessToken) return;
    childrenApi.list(accessToken).then((cs) => {
      if (cs?.[0]) setChildId(cs[0].id);
    });
  }, [childId, accessToken]);

  useEffect(() => {
    if (!accessToken || !childId) return;
    childrenApi.list(accessToken).then((children) => {
      const found = children?.find((c) => c.id === childId);
      if (found) setChild(found);
    });
    childrenApi.getControls(childId, accessToken).then((c) => {
      if (!c) return;
      setDailyMin(c.dailyMinutes);
      setSessionMin(c.sessionMinutes);
      setInteractiveQ(c.interactiveQ);
      setBedtimeLock(c.bedtimeLock);
      setAutoPlay(c.autoPlay);
      setSelectedCats(c.categories);
    });
  }, [childId, accessToken]);

  // Persist on every change
  useEffect(() => {
    if (!accessToken || !childId) return;
    childrenApi.updateControls(
      childId,
      {
        dailyMinutes: dailyMin,
        sessionMinutes: sessionMin,
        interactiveQ,
        bedtimeLock,
        autoPlay,
        categories: selectedCats,
      },
      accessToken,
    );
  }, [
    accessToken,
    childId,
    dailyMin,
    sessionMin,
    interactiveQ,
    bedtimeLock,
    autoPlay,
    selectedCats,
  ]);

  // Store the canonical casing, not lowercase. Category matching is by exact
  // string (Prisma `hasSome`), and the catalog carries the taxonomy's casing, so
  // writing "bedtime" here matched none of the `Bedtime` videos — the
  // restriction looked applied and filtered nothing. Existing lowercase rows are
  // migrated by *_canonicalize_category_casing and canonicalised server-side.
  const toggleCat = (cat: string) => {
    setSelectedCats((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat],
    );
  };

  const handleTab = (key: string) => {
    if (key === "home") navigation.navigate("ParentHome");
    if (key === "library") navigation.navigate("ParentLibrary");
    if (key === "stats") navigation.navigate("Dashboard", { childId });
    if (key === "me") navigation.navigate("Me");
  };

  return (
    <View style={styles.root}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.eyebrow}>
            FOR {(child?.name ?? "...").toUpperCase()}, {child?.age ?? ""}
          </Text>
          <Text style={styles.headline}>Controls</Text>
        </View>

        {/* TIME */}
        <Text style={styles.sectionLabel}>TIME</Text>
        <View style={styles.card}>
          {/* Daily */}
          <View style={styles.rowHead}>
            <Text style={styles.rowTitle}>Daily screen time</Text>
            <Text style={styles.rowDetail}>{dailyMin} min</Text>
          </View>
          <View style={styles.pillRow}>
            {DAILY_OPTIONS.map((v) => (
              <Pill
                key={v}
                label={`${v} min`}
                active={dailyMin === v}
                onPress={() => setDailyMin(v)}
              />
            ))}
          </View>

          <View style={styles.divider} />

          {/* Session */}
          <View style={styles.rowHead}>
            <Text style={styles.rowTitle}>Per-session limit</Text>
            <Text style={styles.rowDetail}>
              {sessionMin === 0 ? "No limit" : `${sessionMin} min`}
            </Text>
          </View>
          <View style={styles.pillRow}>
            {SESSION_OPTIONS.map((v) => {
              const value = v === "noLimit" ? 0 : v;
              return (
                <Pill
                  key={String(v)}
                  label={v === "noLimit" ? "No limit" : `${v} min`}
                  active={sessionMin === value}
                  onPress={() => setSessionMin(value)}
                />
              );
            })}
          </View>

          <View style={styles.divider} />

          {/* Bedtime toggle */}
          <View style={styles.toggleRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.rowTitle}>Bedtime lock</Text>
              <Text style={styles.rowSub}>No videos after 7:30 PM</Text>
            </View>
            <Switch
              value={bedtimeLock}
              onValueChange={setBedtimeLock}
              trackColor={{ false: "rgba(0,0,0,0.15)", true: colors.success }}
              thumbColor="#fff"
            />
          </View>
        </View>

        {/* CONTENT */}
        <Text style={styles.sectionLabel}>CONTENT</Text>
        <View style={styles.card}>
          <View style={styles.contentBlock}>
            <Text style={styles.rowTitle}>Allowed categories</Text>
            <View style={[styles.pillRow, { marginTop: 10 }]}>
              {CATEGORIES.map((c) => {
                const on = selectedCats.includes(c);
                return (
                  <TouchableOpacity
                    key={c}
                    activeOpacity={0.7}
                    onPress={() => toggleCat(c)}
                    style={[
                      styles.catChip,
                      {
                        backgroundColor: on ? colors.ink : "transparent",
                        borderColor: on ? colors.ink : colors.lineStrong,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.catChipText,
                        { color: on ? "#fff" : colors.ink2 },
                      ]}
                    >
                      {c}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          <View style={styles.divider} />

          <View style={styles.toggleRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.rowTitle}>Interactive questions</Text>
              <Text style={styles.rowSub}>Pause for quizzes mid-video</Text>
            </View>
            <Switch
              value={interactiveQ}
              onValueChange={setInteractiveQ}
              trackColor={{ false: "rgba(0,0,0,0.15)", true: colors.success }}
              thumbColor="#fff"
            />
          </View>

          <View style={styles.divider} />

          <View style={styles.toggleRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.rowTitle}>Auto-play next</Text>
              <Text style={styles.rowSub}>
                Start the next recommended video
              </Text>
            </View>
            <Switch
              value={autoPlay}
              onValueChange={setAutoPlay}
              trackColor={{ false: "rgba(0,0,0,0.15)", true: colors.success }}
              thumbColor="#fff"
            />
          </View>
        </View>
      </ScrollView>

      <ParentTabBar active="controls" onChange={handleTab} />
    </View>
  );
}

function Pill({
  label,
  active,
  onPress,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
}) {
  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
      style={[
        styles.pill,
        {
          backgroundColor: active ? colors.primary : "transparent",
          borderColor: active ? colors.primary : colors.lineStrong,
        },
      ]}
    >
      <Text
        style={[
          styles.pillText,
          { color: active ? "#fff" : colors.ink2 },
        ]}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  scroll: { flex: 1 },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 56,
    paddingBottom: TAB_BAR_CLEARANCE,
  },

  // Header
  header: {
    paddingHorizontal: 4,
    marginBottom: 24,
  },
  eyebrow: {
    fontFamily,
    fontSize: 11,
    fontWeight: "800",
    color: colors.ink2,
    letterSpacing: 1.4,
    marginBottom: 4,
  },
  headline: {
    fontFamily,
    fontSize: 30,
    fontWeight: "900",
    color: colors.ink,
    letterSpacing: -0.3,
  },

  sectionLabel: {
    fontFamily,
    fontSize: 11,
    fontWeight: "800",
    color: colors.ink2,
    letterSpacing: 1.4,
    marginBottom: 8,
    paddingHorizontal: 4,
  },

  card: {
    backgroundColor: colors.surface,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.line,
    overflow: "hidden",
    marginBottom: 16,
  },

  rowHead: {
    paddingTop: 14,
    paddingHorizontal: 14,
    paddingBottom: 6,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  rowTitle: {
    fontFamily,
    fontSize: 14,
    fontWeight: "800",
    color: colors.ink,
  },
  rowDetail: {
    fontFamily,
    fontSize: 13,
    fontWeight: "800",
    color: colors.primary,
  },
  rowSub: {
    fontFamily,
    fontSize: 12,
    fontWeight: "600",
    color: colors.ink2,
    marginTop: 1,
  },

  divider: {
    height: 1,
    backgroundColor: colors.line,
    marginVertical: 4,
  },

  pillRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    paddingHorizontal: 14,
    paddingBottom: 14,
  },
  pill: {
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: radii.pill,
    borderWidth: 1.5,
  },
  pillText: {
    fontFamily,
    fontSize: 13,
    fontWeight: "800",
  },

  toggleRow: {
    paddingVertical: 14,
    paddingHorizontal: 14,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },

  contentBlock: {
    paddingTop: 14,
    paddingHorizontal: 14,
    paddingBottom: 4,
  },
  catChip: {
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: radii.pill,
    borderWidth: 1.5,
  },
  catChipText: {
    fontFamily,
    fontSize: 12,
    fontWeight: "800",
  },
});
